// Wave UX-2.4 #23 (2026-05-24) — 낙찰 정산 명세서 (Invoice).
//
// 라우트: /buyer/invoice/:id  (App.js, BuyerProtected)
// 진입: BidsList winner 탭 카드 클릭 → 본 화면
// 데이터: GET /buyer-app/auction/:id 응답의 myWon=true 분기 필드
//   - winnerAmount / transactionFee / sellerContact / sellerBankAccount / pickupNote
// 가드: myWon=false → NOT_WINNER 안내 + dashboard 복귀 버튼
// 인쇄: window.print() (양식 PDF 는 후속 wave — puppeteer 서버 PDF)

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import AuthImage from './AuthImage';
import { authBuyerFetch, isUnauthorized, handleUnauthorized, displayApiError } from './buyerApi';
import { API } from '../../utils/api';
import { useT, useLocale } from '../../i18n/I18nProvider';
import { formatKrwAmount, formatKrwAmountWithConvert, useExchangeRates } from '../../i18n/currency';
import { showToast } from './Toast';
import { confirm } from './ConfirmDialog';

function AuctionBuyerInvoice() {
  var t = useT();
  var loc = useLocale();
  var rateState = useExchangeRates();
  var params = useParams();
  var navigate = useNavigate();
  var auctionId = parseInt(params.id, 10);

  var [auction, setAuction] = useState(null);
  var [loading, setLoading] = useState(true);
  var [notWinner, setNotWinner] = useState(false);

  // Wave TW-1.f.5 (2026-05-24) — 거래 완료 양측 확인 inline 카드
  var [txConfirm, setTxConfirm] = useState(null);
  var [txConfirmBusy, setTxConfirmBusy] = useState(false);
  var loadTxConfirm = function() {
    if (!auctionId) return;
    authBuyerFetch(API + '/buyer-app/transactions/by-source/auction/' + auctionId, {})
      .then(function(r) { return r.json(); })
      .then(function(d) { if (d && d.success) setTxConfirm(d.data || null); })
      .catch(function(){ /* silent */ });
  };

  useEffect(function() {
    if (!auctionId) { setLoading(false); return; }
    authBuyerFetch(API + '/buyer-app/auction/' + auctionId, {})
      .then(function(r) {
        if (isUnauthorized(r)) { handleUnauthorized(navigate); throw new Error('unauthorized'); }
        if (r.status === 404) { setNotWinner(true); throw new Error('not-found'); }
        return r.json();
      })
      .then(function(d) {
        if (!d || !d.success) throw new Error('load-fail');
        var a = d.data && d.data.auction;
        if (!a || !a.myWon) { setNotWinner(true); return; }
        setAuction(a);
        loadTxConfirm();
      })
      .catch(function(){ /* notWinner/unauthorized 분기에서 처리 */ })
      .finally(function() { setLoading(false); });
  }, [auctionId, navigate]);

  var onTxConfirmClick = function() {
    if (!txConfirm || txConfirmBusy) return;
    confirm({ message: t('txConfirmPrompt'), variant: 'primary' }).then(function(ok) {
      if (!ok) return;
      setTxConfirmBusy(true);
      authBuyerFetch(API + '/buyer-app/transactions/' + txConfirm.id + '/confirm', { method: 'POST' })
        .then(function(r) { return r.json(); })
        .then(function(d) {
          setTxConfirmBusy(false);
          if (d && d.success) {
            setTxConfirm(d.data || null);
            showToast(d.completed ? t('txConfirmToastBoth') : t('txConfirmToastWait'), { variant: 'success' });
          } else {
            showToast(displayApiError(d, t, 'txConfirmFail'), { variant: 'error' });
          }
        })
        .catch(function() {
          setTxConfirmBusy(false);
          showToast(t('commonNetwork'), { variant: 'error' });
        });
    });
  };

  function fmt(amount) {
    if (amount == null) return '';
    return formatKrwAmountWithConvert(amount, t, loc, rateState);
  }

  function copyBank() {
    var bank = auction && auction.sellerBankAccount;
    if (!bank) return;
    var text = (bank.bank || '') + ' ' + (bank.account || '') + ' ' + (bank.holder || '');
    text = text.trim();
    var done = function(){ showToast(t('invoiceCopied'), { variant: 'success' }); };
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(function(){ done(); });
        return;
      }
    } catch (e) { /* fall through */ }
    // legacy fallback
    try {
      var ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
      done();
    } catch (e) { /* silent */ }
  }

  function onPrint() {
    try { window.print(); } catch (e) { /* silent */ }
  }

  if (loading) {
    return (
      <AuctionBuyerLayout activeKey="bids">
        <div style={{ padding: 24, color: '#94a3b8', textAlign: 'center' }}>{t('invoiceLoading')}</div>
      </AuctionBuyerLayout>
    );
  }

  if (notWinner || !auction) {
    return (
      <AuctionBuyerLayout activeKey="bids">
        <div style={{ padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 36 }}>📄</div>
          <div style={{ marginTop: 12, fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>{t('invoiceNotWinner')}</div>
          <button type="button" onClick={function(){ navigate('/buyer/bids'); }}
            style={{ marginTop: 20, padding: '10px 22px', border: 'none', borderRadius: 10, background: '#1B3A5C', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            {t('invoiceBack')}
          </button>
        </div>
      </AuctionBuyerLayout>
    );
  }

  var car = auction.car || {};
  var fee = auction.transactionFee || { amount: 0, dueDays: 7 };
  var seller = auction.sellerContact || {};
  var bank = auction.sellerBankAccount || null;
  var total = (auction.winnerAmount || 0) + (fee.amount || 0);
  var photoPath = (auction.photos && auction.photos.length > 0)
    ? ('/buyer-app/auction/' + auction.id + '/photo/0')
    : null;

  return (
    <AuctionBuyerLayout activeKey="bids">
      <div className="buyer-invoice-root" style={{ maxWidth: 720, margin: '0 auto', padding: '16px 18px 40px' }}>
        {/* 인쇄용 CSS — 헤더/탭/사이드 숨김, 본문만 출력 */}
        <style>{`
          @media print {
            body * { visibility: hidden; }
            .buyer-invoice-root, .buyer-invoice-root * { visibility: visible; }
            .buyer-invoice-root { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; }
            .buyer-invoice-noprint { display: none !important; }
          }
        `}</style>

        {/* header */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
          <h1 style={{ flex: 1, fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>{t('invoiceTitle')}</h1>
          <button type="button" onClick={onPrint}
            className="buyer-invoice-noprint"
            style={{ padding: '8px 14px', border: '1px solid #cbd5e1', borderRadius: 8, background: 'white', color: '#475569', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            🖨 {t('invoicePrint')}
          </button>
        </div>

        {/* Wave TW-1.f.5 (2026-05-24) — 거래 완료 양측 확인 inline 카드 (인쇄 시 숨김) */}
        {txConfirm && (
          <div className="buyer-invoice-noprint"
            style={{
              background: txConfirm.completed_at ? '#f0fdf4' : '#eff6ff',
              border: '1px solid ' + (txConfirm.completed_at ? '#86efac' : '#bfdbfe'),
              borderRadius: 10, padding: 14, marginBottom: 14,
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>📋 {t('txConfirmTitle')}</div>
              {txConfirm.reminder_count > 0 && (
                <span style={{ fontSize: 10, color: '#b45309', background: '#fef3c7', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                  {t('txConfirmReminderBadge', { n: txConfirm.reminder_count })}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 14, fontSize: 11, marginBottom: 10 }}>
              <span style={{ color: txConfirm.seller_confirmed_at ? '#15803d' : '#94a3b8' }}>
                {txConfirm.seller_confirmed_at ? t('txConfirmSellerConfirmed') : t('txConfirmSellerPending')}
              </span>
              <span style={{ color: txConfirm.buyer_confirmed_at ? '#15803d' : '#94a3b8' }}>
                {txConfirm.buyer_confirmed_at ? t('txConfirmBuyerConfirmed') : t('txConfirmBuyerPending')}
              </span>
            </div>
            {txConfirm.completed_at ? (
              <div style={{ fontSize: 12, fontWeight: 700, color: '#16a34a' }}>
                {t('txConfirmBothCompleted')} ({txConfirm.completed_at})
              </div>
            ) : (
              <button type="button" onClick={onTxConfirmClick} disabled={!!txConfirm.buyer_confirmed_at || txConfirmBusy}
                style={{
                  width: '100%', padding: '10px 0',
                  background: txConfirm.buyer_confirmed_at ? '#e2e8f0' : '#1B3A5C',
                  color: txConfirm.buyer_confirmed_at ? '#64748b' : 'white',
                  border: 'none', borderRadius: 8,
                  fontSize: 13, fontWeight: 700,
                  cursor: txConfirm.buyer_confirmed_at ? 'default' : 'pointer',
                }}>
                {txConfirm.buyer_confirmed_at ? t('txConfirmCompleted') : (txConfirmBusy ? t('txConfirmBusy') : t('txConfirmActionLabel'))}
              </button>
            )}
          </div>
        )}

        {/* 1. 차량 */}
        <Section title={t('invoiceSectionCar')}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
            {photoPath && (
              <div style={{ width: 96, height: 72, borderRadius: 8, overflow: 'hidden', background: '#f1f5f9', flexShrink: 0 }}>
                <AuthImage path={photoPath} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                {(car.year ? car.year + ' ' : '') + (car.model || '')}
              </div>
              <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>
                {car.number ? car.number : ''}
                {car.vin ? ' · VIN ' + car.vin : ''}
              </div>
            </div>
          </div>
        </Section>

        {/* 2. 정산 금액 */}
        <Section title={t('invoiceSectionAmount')}>
          <Row label={t('invoiceWinnerAmount')} value={fmt(auction.winnerAmount)} />
          <Row label={t('invoiceFee')} value={fmt(fee.amount)} />
          <Row label={t('invoiceTotal')} value={fmt(total)} bold />
        </Section>

        {/* 3. 출품자 */}
        <Section title={t('invoiceSectionSeller')}>
          <Row label="" value={seller.companyName} />
          {seller.ceoName && <Row label="" value={seller.ceoName} />}
          {seller.phone && <Row label="" value={seller.phone} dir="ltr" />}
          {seller.address && <Row label="" value={seller.address} multiline />}
        </Section>

        {/* 4. 송금 계좌 */}
        {bank && (
          <Section title={t('invoiceSectionBank')}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: '#64748b' }}>{bank.bank || ''}</div>
                <div style={{ marginTop: 2, fontSize: 15, fontWeight: 700, color: '#0f172a' }} dir="ltr">{bank.account || ''}</div>
                <div style={{ marginTop: 2, fontSize: 12, color: '#94a3b8' }}>{bank.holder || ''}</div>
              </div>
              <button type="button" onClick={copyBank}
                className="buyer-invoice-noprint"
                style={{ padding: '8px 14px', border: '1px solid #cbd5e1', borderRadius: 8, background: '#f8fafc', color: '#1B3A5C', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                📋 {t('invoiceCopyBank')}
              </button>
            </div>
          </Section>
        )}

        {/* 5. 결제 기한 */}
        <Section title={t('invoiceSectionDue')}>
          <Row label="" value={t('invoiceDueDays', { n: fee.dueDays || 7 })} bold />
        </Section>

        {/* 6. 인계 안내 */}
        {auction.pickupNote && (
          <Section title={t('invoiceSectionPickup')}>
            <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
              {auction.pickupNote}
            </div>
          </Section>
        )}
      </div>
    </AuctionBuyerLayout>
  );
}

function Section(props) {
  return (
    <div style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, letterSpacing: '0.04em', marginBottom: 10, textTransform: 'uppercase' }}>
        {props.title}
      </div>
      {props.children}
    </div>
  );
}

function Row(props) {
  return (
    <div style={{
      display: 'flex', alignItems: props.multiline ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      padding: '6px 0',
      fontSize: 14,
    }}>
      {props.label && <span style={{ color: '#64748b', fontSize: 13 }}>{props.label}</span>}
      <span style={{
        flex: props.label ? '0 0 auto' : 1,
        textAlign: props.label ? 'right' : 'left',
        color: '#0f172a',
        fontWeight: props.bold ? 800 : 600,
        fontSize: props.bold ? 16 : 14,
        whiteSpace: props.multiline ? 'pre-wrap' : 'normal',
        lineHeight: props.multiline ? 1.6 : 1.4,
      }}
      dir={props.dir || undefined}>
        {props.value || '-'}
      </span>
    </div>
  );
}

export default AuctionBuyerInvoice;
